import React, { useState } from 'react';
import { Formik } from 'formik';
import useTranslation from 'next-translate/useTranslation';
import { toast } from 'react-toastify';
import { camelCase } from 'case-anything';
import { getCountries } from '@services/country-repository';
import { RegisterLayout } from '@/components/Layouts/RegisterLayout';
import { RegisterBusinessAccount } from './components/RegisterBusinessAccount';
import { SuccessMessage } from './components/SuccessMessage';
import * as schemas from './form-schemas';
import { registerClient } from './registerClient';
import styles from './register.module.scss';
import type { FormikHelpers, FormikValues } from 'formik';

interface IRegister {
  countryData: ICountry[];
}

const businessAccountSchema = schemas.businessAccount.concat(schemas.loginData);

const Register = ({ countryData }: IRegister) => {
  const { t } = useTranslation('register');
  const [success, setSuccess] = useState(false);

  const handleFormSubmit = async (values: FormikValues, actions: FormikHelpers<any>) => {
    try {
      // Remove fields not part of the form
      const formData = businessAccountSchema.noUnknown().cast(values);
      await registerClient.registerBusinessAccount(formData);

      actions.resetForm();
      actions.setSubmitting(false);
      setSuccess(true);
    } catch (error: any) {
      const errorResponse = error.response;

      if (errorResponse?.data.errors) {
        const validationErrors: JSONObject = {};

        Object.entries(errorResponse.data.errors).forEach(([key, value]: any) => {
          const validationKey = camelCase(key);
          validationErrors[validationKey] = value[0];
        });

        actions.setErrors(validationErrors as any);
        toast(t('common:form-fields-error-message') as string);
      } else if (errorResponse?.status === 409 && errorResponse.data?.code === 'UsernameAlreadyExists') {
        actions.setErrors({ username: errorResponse.data.title });
        toast(t('common:form-fields-error-message') as string);
      } else {
        toast.error(t('common:unhelpful-error-message') as string);
      }
    } finally {
      window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    }
  };

  return success ? (
    <SuccessMessage />
  ) : (
    <Formik
      validationSchema={businessAccountSchema}
      onSubmit={handleFormSubmit}
      initialValues={businessAccountSchema.cast({})}
      validateOnChange={false}
      validateOnBlur={false}
    >
      <div className={styles.registerFormContainer}>
        <div className="border border-bottom-0">
          <h1>{t('title.register')}</h1>
        </div>
        <div>
          <RegisterBusinessAccount countryData={countryData} />
        </div>
      </div>
    </Formik>
  );
};

export async function getServerSideProps() {
  const countryData = await getCountries();

  return {
    props: {
      countryData
    }
  };
}

Register.getLayout = function getLayout(page: React.ReactNode) {
  return <RegisterLayout>{page}</RegisterLayout>;
};

export default Register;
